import * as Sentry from '@sentry/nextjs'
import { marketplaceListProductsQueryFetcher } from '@web/gen/content'
import { MarketplaceLanguage, MarketplaceProductSortOrder } from '@web/gen/types'
import { LANGUAGE_COOKIE_NAME } from 'constants/cookies'
import type { LocalizedStrings } from 'codegen/types'
import type { SortType } from '../../_components/legacy-product-category-page/pagination/sort'
import { AppRoutes } from '@repo/web-utils/app-routes'

type MarketplaceListProductsResponse = Awaited<
  ReturnType<ReturnType<typeof marketplaceListProductsQueryFetcher>>
>

type MarketplaceBreadcrumb = NonNullable<
  NonNullable<
    NonNullable<MarketplaceListProductsResponse['marketplaceListProducts']>['categoryPageData']
  >['breadcrumb']
>[number]

type MarketplaceRelatedCategory = NonNullable<
  NonNullable<
    NonNullable<MarketplaceListProductsResponse['marketplaceListProducts']>['categoryPageData']
  >['relatedCategories']
>[number]

export type MarketplaceProductsPayload = NonNullable<
  MarketplaceListProductsResponse['marketplaceListProducts']
>

type MarketplaceProduct = NonNullable<MarketplaceProductsPayload['products']>[number]

export interface GraphicsCategoryProduct {
  productId?: number | null
  productUrl?: string | null
  productImage?: string | null
  imageAlt?: string | null
  productTitle?: string | null
  categoryUrl?: string | null
  productTitleLocalized?: LocalizedStrings | null
  cardCategory?: LocalizedStrings | null
  productCountdown?: string | null
}

function getHeaders(locale: string) {
  return new Headers({
    Cookie: `${LANGUAGE_COOKIE_NAME}=${locale}`,
  })
}

export async function getMarketplaceProducts(params: {
  locale: string
  slug: string
  limit: number
  page?: number
  orderBy?: MarketplaceProductSortOrder
}) {
  const fetcher = marketplaceListProductsQueryFetcher(
    {
      input: {
        categorySlug: params.slug,
        locale: mapLocaleToMarketplaceLanguage(params.locale),
        pagination: {
          currentPage: params.page ?? 1,
          pageSize: params.limit,
        },
        sortType: params.orderBy ?? MarketplaceProductSortOrder.ProductSortOrderMostPopular,
      },
    },
    {
      headers: getHeaders(params.locale),
    },
  )

  try {
    return await fetcher()
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}

export function getMarketplaceProductsPayload(
  response: MarketplaceListProductsResponse | null | undefined,
) {
  return response?.marketplaceListProducts ?? null
}

export function setSortingOptions(orderBy: SortType | string): MarketplaceProductSortOrder {
  switch (orderBy) {
    case 'date': {
      return MarketplaceProductSortOrder.ProductSortOrderMostRecent
    }
    default: {
      return MarketplaceProductSortOrder.ProductSortOrderMostPopular
    }
  }
}

function mapLocaleToMarketplaceLanguage(locale: string): MarketplaceLanguage {
  switch (locale) {
    case 'de': {
      return MarketplaceLanguage.LanguageDe
    }
    case 'es': {
      return MarketplaceLanguage.LanguageEs
    }
    case 'fr': {
      return MarketplaceLanguage.LanguageFr
    }
    case 'it': {
      return MarketplaceLanguage.LanguageIt
    }
    case 'nl': {
      return MarketplaceLanguage.LanguageNl
    }
    case 'pl': {
      return MarketplaceLanguage.LanguagePl
    }
    case 'pt': {
      return MarketplaceLanguage.LanguagePt
    }
    default: {
      return MarketplaceLanguage.LanguageEn
    }
  }
}

function getImageUrl(product: MarketplaceProduct) {
  return (
    product.mainImageSizes?.woocommerceSingle?.url ||
    product.mainImageSizes?.woocommerceThumbnail?.url ||
    product.mainImageSizes?.shopSingle?.url ||
    product.mainImageSizes?.mainImage?.url ||
    product.productImage ||
    ''
  )
}

function formatCategoryLabel(value: string) {
  return value
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/\b3d\b/gi, '3D')
}

function getCategoryLabel(cardCategory: unknown, locale: string) {
  if (typeof cardCategory === 'string') {
    return cardCategory.trim()
  }

  if (typeof cardCategory === 'object' && cardCategory !== null) {
    const localized = (cardCategory as Record<string, unknown>)[locale]
    if (typeof localized === 'string') {
      return localized.trim()
    }
  }

  return ''
}

function getCategorySlug(cardCategory: unknown) {
  if (typeof cardCategory === 'string') {
    return cardCategory.trim().toLowerCase()
  }

  return ''
}

export function normalizeMarketplaceProductUrl(productUrl?: string | null) {
  if (!productUrl) {
    return ''
  }

  const normalized = productUrl.trim()
  const withBase = normalized.startsWith('http')
    ? normalized
    : `https://creativefabrica.com${normalized.startsWith('/') ? normalized : `/${normalized}`}`

  try {
    const { pathname } = new URL(withBase)
    const segments = pathname.split('/').filter(Boolean)
    const productSegmentIndex = segments.findIndex(segment => segment === 'product')

    if (productSegmentIndex >= 0) {
      const productSlug = segments[productSegmentIndex + 1]
      return productSlug ? `/product/${productSlug}/` : ''
    }

    const lastPathSegment = segments.at(-1)
    return lastPathSegment ? `/product/${lastPathSegment}/` : ''
  } catch {
    const fallbackMatch = normalized.match(/\/product\/([^/?#]+)/)
    if (fallbackMatch?.[1]) {
      return `/product/${fallbackMatch[1]}/`
    }

    const fallbackSegments = normalized.split('/').filter(Boolean)
    const lastPathSegment = fallbackSegments.at(-1)?.split('?')[0]
    return lastPathSegment ? `/product/${lastPathSegment}/` : ''
  }
}

function mapMarketplaceProductToCategoryProduct(
  product: MarketplaceProduct,
  locale: string,
  categoryUrlPrefix = '/graphics/',
): GraphicsCategoryProduct {
  const categoryLabel = getCategoryLabel(product.cardCategory, locale)
  const categorySlug = getCategorySlug(product.cardCategory)

  return {
    productId: Number(product.legacyProductId || product.productId || 0),
    productUrl: normalizeMarketplaceProductUrl(product.productUrl),
    productImage: getImageUrl(product),
    imageAlt: product.productTitle || '',
    productTitle: product.productTitle || '',
    productCountdown: product.productCountdown,
    cardCategory: categoryLabel
      ? ({ [locale]: formatCategoryLabel(categoryLabel) } as LocalizedStrings)
      : null,
    categoryUrl: categorySlug ? `${categoryUrlPrefix}${categorySlug}/` : null,
  }
}

export function mapMarketplaceProductsToCategoryProducts(
  products: MarketplaceProduct[] | null | undefined,
  locale: string,
  categoryUrlPrefix = '/graphics/',
) {
  return (products || []).map(product =>
    mapMarketplaceProductToCategoryProduct(product, locale, categoryUrlPrefix),
  )
}

export function mapMarketplaceCategoryLinks(
  relatedCategories: MarketplaceRelatedCategory[] | null | undefined,
) {
  return (
    relatedCategories
      ?.filter(category => category.title && category.slug)
      .map(category => ({
        name: category.title,
        slug: AppRoutes.SUBSCRIPTIONS_GRAPHICS + category.slug,
      })) ?? []
  )
}

export function getMarketplacePageTitle(
  payload: MarketplaceProductsPayload | null | undefined,
  fallbackTitle: string,
) {
  return (
    payload?.categoryPageData?.breadcrumb?.at(-1)?.title ||
    payload?.categoryPageData?.pageContent?.title ||
    fallbackTitle
  )
}

export function mapMarketplaceBreadcrumbs(breadcrumbs: MarketplaceBreadcrumb[] | null | undefined) {
  const segments: string[] = []

  return (breadcrumbs || [])
    .filter(item => item.title)
    .map((breadcrumb, index, items) => {
      if (breadcrumb.slug) {
        segments.push(breadcrumb.slug)
      }

      return {
        name: breadcrumb.title,
        slug:
          index < items.length - 1 && segments.length > 0 ? `/${segments.join('/')}/` : undefined,
      }
    })
}
